import { useEffect, useRef } from 'react';

// ---- Axis helpers ------------------------------------------------------
//
// Drop-in axis components rendered as positioned HTML so they don't
// stretch with the chart's preserveAspectRatio="none" SVG/canvas. The
// chart parent should be `position: relative` for the freq axis to
// anchor correctly; the time axis sits in normal flow under the chart.

/** "Nice" tick step (1, 2, or 5 × 10^k) so we get ~target ticks. */
function niceTickStep(rangeSec: number, target = 6): number {
  if (rangeSec <= 0) return 1;
  const rough = rangeSec / target;
  const exp = Math.floor(Math.log10(rough));
  const mant = rough / Math.pow(10, exp);
  const nice = mant < 1.5 ? 1 : mant < 3 ? 2 : mant < 7 ? 5 : 10;
  return nice * Math.pow(10, exp);
}

function fmtTime(t: number): string {
  if (Math.abs(t) < 1e-9) return '0';
  if (Math.abs(t) < 1) return `${(t * 1000).toFixed(0)} ms`;
  return Math.abs(t) < 10 ? `${t.toFixed(1)} s` : `${t.toFixed(0)} s`;
}

function fmtHz(f: number): string {
  if (f >= 1000) return `${(f / 1000).toFixed(f >= 10000 ? 0 : 1)} kHz`;
  return `${f.toFixed(f < 10 ? 1 : 0)} Hz`;
}

interface TimeAxisProps {
  /** Seconds covered by ONE segment. All segments are assumed equal. */
  segmentSec: number;
  /** Number of repeating segments laid out side-by-side; default 1. */
  numSegments?: number;
  /** Pixels of horizontal gap between consecutive segments (matches the
   *  chart's segmentGapPx, in the same units as the chart's viewBox). */
  segmentGapUnits?: number;
  /** "Width" of one segment in chart-units (e.g. SVG viewBox units, or
   *  numBlocks for spectrograms). Used to compute proportional positions
   *  when segmentGapUnits > 0. Default 1 (segments share width equally). */
  segmentWidthUnits?: number;
  /** Compact mode for tiny mini-charts: just shows "0 — N s" at the
   *  extremes, no intermediate ticks. */
  compact?: boolean;
}

export function TimeAxis({
  segmentSec, numSegments = 1, segmentGapUnits = 0,
  segmentWidthUnits = 1, compact = false,
}: TimeAxisProps) {
  const totalUnits = numSegments * segmentWidthUnits + (numSegments - 1) * segmentGapUnits;
  const step = compact ? segmentSec : niceTickStep(segmentSec, 5);
  const ticks: number[] = [];
  if (compact) {
    ticks.push(0, segmentSec);
  } else {
    for (let t = 0; t <= segmentSec + 1e-6; t += step) {
      ticks.push(Math.round(t / step) * step);   // de-jitter from FP add
    }
  }
  return (
    <div
      style={{
        position: 'relative', height: compact ? 14 : 22, marginTop: 2,
        fontSize: '0.7rem', color: 'var(--muted)',
      }}
      aria-label="time axis"
    >
      {Array.from({ length: numSegments }, (_, segIdx) => {
        const segLeftUnits = segIdx * (segmentWidthUnits + segmentGapUnits);
        return ticks.map((t) => {
          const offsetUnits = (t / segmentSec) * segmentWidthUnits;
          const leftPct = ((segLeftUnits + offsetUnits) / totalUnits) * 100;
          // Clip the rightmost label so it doesn't overflow into the next
          // segment's gap or the right edge.
          const isFirst = t === 0;
          const isLast  = Math.abs(t - segmentSec) < 1e-6;
          const align: React.CSSProperties = isFirst
            ? { transform: 'translateX(0)' }
            : isLast
              ? { transform: 'translateX(-100%)' }
              : { transform: 'translateX(-50%)' };
          return (
            <div
              key={`${segIdx}-${t}`}
              style={{
                position: 'absolute', left: `${leftPct}%`, top: 0,
                ...align,
              }}
            >
              <div style={{
                width: 1, height: 4, background: 'var(--border)',
                margin: isFirst ? '0 0 0 0' : isLast ? '0 0 0 auto' : '0 auto',
              }} />
              <div style={{ marginTop: 1, whiteSpace: 'nowrap' }}>{fmtTime(t)}</div>
            </div>
          );
        });
      })}
    </div>
  );
}

interface FreqAxisProps {
  minHz: number;
  maxHz: number;
  /** Total chart height in CSS px (must match the canvas/SVG height). */
  height: number;
  /** Use log spacing for the ticks (typical for wavelet). */
  log?: boolean;
  /** Visual width of the axis column in CSS px; default 44. */
  width?: number;
}

export function FreqAxis({
  minHz, maxHz, height, log = false, width = 44,
}: FreqAxisProps) {
  // Pick ~6 ticks. For log: every decade plus 2× midpoints. For linear:
  // niceTickStep on Hz.
  const ticks: number[] = [];
  if (log) {
    const lo = Math.log10(Math.max(1, minHz));
    const hi = Math.log10(maxHz);
    for (let exp = Math.floor(lo); exp <= Math.ceil(hi); exp++) {
      for (const mant of [1, 2, 5]) {
        const f = mant * Math.pow(10, exp);
        if (f >= minHz && f <= maxHz) ticks.push(f);
      }
    }
  } else {
    const step = niceTickStep(maxHz - minHz, 6);
    for (let f = Math.ceil(minHz / step) * step; f <= maxHz + 1e-6; f += step) {
      ticks.push(Math.round(f / step) * step);
    }
  }
  const positionPct = (f: number): number => {
    // 0% = top (high freq), 100% = bottom (low freq).
    if (log) {
      const lo = Math.log10(Math.max(1, minHz));
      const hi = Math.log10(maxHz);
      return (1 - (Math.log10(f) - lo) / (hi - lo)) * 100;
    }
    return (1 - (f - minHz) / (maxHz - minHz)) * 100;
  };
  return (
    <div
      style={{
        width, height, position: 'relative', flexShrink: 0,
        fontSize: '0.7rem', color: 'var(--muted)',
        borderRight: '1px solid var(--border)',
      }}
      aria-label="frequency axis"
    >
      {ticks.map((f) => {
        const top = positionPct(f);
        return (
          <div
            key={f}
            style={{
              position: 'absolute', right: 4, top: `${top}%`,
              transform: 'translateY(-50%)', whiteSpace: 'nowrap',
            }}
          >{fmtHz(f)}</div>
        );
      })}
    </div>
  );
}

interface Props {
  rms: number[];
  height?: number;
  /** Recording duration in seconds — when set, draws a compact 0—Ns
   *  time axis below. */
  durationSec?: number;
}

export function RmsChart({ rms, height = 80, durationSec }: Props) {
  if (!rms.length) return <div className="muted">no data</div>;
  const w = 600;
  const max = Math.max(0.0001, ...rms);
  const svg = (
    <svg
      className="chart"
      viewBox={`0 0 ${w} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={`RMS chart, ${rms.length} blocks, peak ${max.toFixed(4)}`}
    >
      {rms.map((v, i) => {
        const x = (i / rms.length) * w;
        const bw = w / rms.length;
        const h = (v / max) * height;
        return (
          <rect
            key={i}
            x={x} y={height - h}
            width={Math.max(0.5, bw - 0.4)}
            height={h}
            fill="var(--accent)"
          />
        );
      })}
    </svg>
  );
  if (durationSec == null) return svg;
  return (
    <div>
      {svg}
      <TimeAxis segmentSec={durationSec} compact />
    </div>
  );
}

interface LiveRmsProps {
  rms: number[];
  height?: number;
  clipBoundaries?: number[];
  /** Seconds per segment; when set, a TimeAxis is rendered below. */
  segmentSec?: number;
  /** Defaults to (clipBoundaries.length + 1) — the number of clips
   *  represented in `rms`. */
  numSegments?: number;
}

export function LiveRmsChart({
  rms, height = 120, clipBoundaries = [], segmentSec, numSegments,
}: LiveRmsProps) {
  if (rms.length < 2) {
    return <div className="muted" style={{ padding: '1rem 0' }}>collecting samples…</div>;
  }
  const w = 1200;
  const max = Math.max(0.0001, ...rms);
  const denom = rms.length - 1;
  const points = rms
    .map((v, i) => `${((i / denom) * w).toFixed(2)},${(height - (v / max) * height).toFixed(2)}`)
    .join(' ');
  const svg = (
    <svg
      className="chart"
      viewBox={`0 0 ${w} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={`Live RMS, ${rms.length} blocks, peak ${max.toFixed(4)}`}
      style={{ height }}
    >
      {clipBoundaries.map((idx, i) => {
        if (idx <= 0 || idx >= rms.length) return null;
        const x = (idx / denom) * w;
        return (
          <line
            key={i}
            x1={x} y1={0} x2={x} y2={height}
            stroke="var(--border)" strokeWidth="1" strokeDasharray="3 3"
          />
        );
      })}
      <polyline
        points={points}
        fill="none"
        stroke="var(--accent)"
        strokeWidth="1.5"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
  if (segmentSec == null) return svg;
  const nSeg = numSegments ?? (clipBoundaries.length + 1);
  return (
    <div>
      {svg}
      <TimeAxis segmentSec={segmentSec} numSegments={nSeg} />
    </div>
  );
}

export interface SpectrogramSegment {
  /** Block-major flat dB values; length === numBlocks * numBins. */
  data: number[];
  numBlocks: number;
  numBins: number;
}

interface SpectrogramProps {
  segments: SpectrogramSegment[];
  height?: number;
  /** dB clamped to this range, then mapped to viridis. */
  dbMin?: number;
  dbMax?: number;
  /** Width in canvas-px between consecutive segments — visually marks
   *  non-continuous time between recordings. */
  segmentGapPx?: number;
  /** Seconds covered by each segment — when set, draws a TimeAxis below. */
  segmentSec?: number;
  /** When set, draws a frequency axis on the left (0 → fs/2 Hz). */
  sampleRate?: number;
}

/**
 * Canvas-based spectrogram renderer.
 *
 * Each segment's blocks are drawn as adjacent vertical columns. Between
 * segments we leave a gap (filled with a contrasting separator colour) so
 * it is visually clear that the recordings are sampled at intervals — the
 * time axis is not continuous across segment boundaries.
 *
 * Frequency goes low (DC) at the bottom to high (Nyquist) at the top.
 */
export function LiveSpectrogramChart({
  segments,
  height = 200,
  dbMin = -90,
  dbMax = 0,
  segmentGapPx = 6,
  segmentSec,
  sampleRate,
}: SpectrogramProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (segments.length === 0 || segments[0].numBlocks === 0) {
      canvas.width = 1; canvas.height = 1;
      const ctx = canvas.getContext('2d');
      ctx?.clearRect(0, 0, 1, 1);
      return;
    }

    const numBins = segments[0].numBins;
    const totalBlocks = segments.reduce((n, s) => n + s.numBlocks, 0);
    const totalCols = totalBlocks + Math.max(0, segments.length - 1) * segmentGapPx;
    const W = Math.max(1, totalCols);
    const H = Math.max(1, numBins);

    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Light-gray separator color in the gaps between segments. Distinct from
    // any colour viridis can produce, so gaps read as "non-data".
    ctx.fillStyle = '#cbd5e1';
    ctx.fillRect(0, 0, W, H);

    const img = ctx.createImageData(W, H);
    // Pre-fill the whole image with the separator colour, then we'll
    // overwrite the data columns.
    for (let i = 0; i < img.data.length; i += 4) {
      img.data[i] = 0xcb;
      img.data[i + 1] = 0xd5;
      img.data[i + 2] = 0xe1;
      img.data[i + 3] = 0xff;
    }

    let x = 0;
    for (let s = 0; s < segments.length; s++) {
      const seg = segments[s];
      for (let b = 0; b < seg.numBlocks; b++) {
        const offset = b * seg.numBins;
        for (let f = 0; f < seg.numBins; f++) {
          const db = seg.data[offset + f];
          const t = Math.max(0, Math.min(1, (db - dbMin) / (dbMax - dbMin)));
          const [R, G, B] = viridis(t);
          const y = H - 1 - f;     // low frequency at bottom
          const idx = (y * W + x) * 4;
          img.data[idx]     = R;
          img.data[idx + 1] = G;
          img.data[idx + 2] = B;
          img.data[idx + 3] = 0xff;
        }
        x++;
      }
      if (s < segments.length - 1) x += segmentGapPx;
    }
    ctx.putImageData(img, 0, 0);
  }, [segments, dbMin, dbMax, segmentGapPx]);

  const canvas = (
    <canvas
      ref={canvasRef}
      className="chart"
      style={{
        width: '100%',
        height,
        imageRendering: 'pixelated',
        background: '#cbd5e1',
        display: 'block',
      }}
      aria-label="spectrogram"
    />
  );

  if (segmentSec == null && sampleRate == null) return canvas;

  // Wrap with frequency axis (left) and/or time axis (below).
  const blocksPerSeg = segments[0]?.numBlocks ?? 1;
  return (
    <div>
      <div style={{ display: 'flex' }}>
        {sampleRate != null && (
          <FreqAxis minHz={0} maxHz={sampleRate / 2} height={height} log={false} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>{canvas}</div>
      </div>
      {segmentSec != null && (
        <div style={{
          display: 'flex',
          marginLeft: sampleRate != null ? 44 : 0,   // align with chart, skip axis col
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <TimeAxis
              segmentSec={segmentSec}
              numSegments={segments.length}
              segmentGapUnits={segmentGapPx}
              segmentWidthUnits={blocksPerSeg}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export interface ComponentsSegment {
  /** num_components arrays, each display_length samples. */
  components: number[][];
}

interface ComponentsProps {
  segments: ComponentsSegment[];
  /** Total chart height. Each component lane gets height / num_components. */
  height?: number;
  /** Pixels of horizontal gap between consecutive segments — visual marker
   *  that the time axis is non-continuous between recordings. */
  segmentGapPx?: number;
  /** Seconds per segment — when set, draws a TimeAxis below. */
  segmentSec?: number;
}

/**
 * Stacked-row chart of SVD components over the rolling window of segments.
 *
 * One row per component (labeled comp1..compN, where N is whatever Octave
 * returned — not hardcoded). Each row plots that component's waveform from
 * each segment side-by-side, with a contrasting gap between segments to
 * indicate the (non-continuous) pause between recordings.
 *
 * Y-scale per row is independent (each component normalized to its own
 * min/max across the visible window) so a small-variance component is
 * still legible alongside a dominant one.
 */
export function LiveComponentsChart({
  segments,
  height = 260,
  segmentGapPx = 6,
  segmentSec,
}: ComponentsProps) {
  if (segments.length === 0) {
    return <div className="muted" style={{ padding: '1rem 0' }}>collecting samples…</div>;
  }
  const numComps = segments[0].components.length;
  if (numComps === 0) {
    return <div className="muted" style={{ padding: '1rem 0' }}>no components yet</div>;
  }
  const dispLen = segments[0].components[0]?.length ?? 0;
  if (dispLen === 0) {
    return <div className="muted" style={{ padding: '1rem 0' }}>empty component</div>;
  }
  const totalCols = segments.length * dispLen + Math.max(0, segments.length - 1) * segmentGapPx;
  const W = Math.max(1, totalCols);
  const laneH = height / numComps;
  const traceTop = 4;
  const traceBottom = 4;
  const traceH = Math.max(1, laneH - traceTop - traceBottom);

  // Per-component y-range across the full visible window (so the trace is
  // stable as new segments arrive — only changes when the extremes change).
  const ranges = Array.from({ length: numComps }, (_, k) => {
    let mn = Infinity, mx = -Infinity;
    for (const seg of segments) {
      const arr = seg.components[k];
      if (!arr) continue;
      for (const v of arr) {
        if (v < mn) mn = v;
        if (v > mx) mx = v;
      }
    }
    if (!Number.isFinite(mn) || !Number.isFinite(mx)) { mn = -1; mx = 1; }
    if (mx - mn < 1e-12) { mn -= 1e-12; mx += 1e-12; }
    return { mn, range: mx - mn };
  });

  const svg = (
    <svg
      viewBox={`0 0 ${W} ${height}`}
      preserveAspectRatio="none"
      style={{ width: '100%', height, display: 'block' }}
      aria-label={`SVD components, ${numComps} lanes, ${segments.length} segments`}
    >
      {/* Segment-gap bars: contrasting fill so the discontinuity is obvious */}
      {segments.slice(0, -1).map((_, sIdx) => {
        const x = (sIdx + 1) * dispLen + sIdx * segmentGapPx;
        return (
          <rect
            key={`gap-${sIdx}`}
            x={x} y={0}
            width={segmentGapPx} height={height}
            fill="#cbd5e1"
          />
        );
      })}
      {/* Lane separators */}
      {Array.from({ length: numComps - 1 }, (_, i) => {
        const y = (i + 1) * laneH;
        return (
          <line
            key={`sep-${i}`}
            x1={0} y1={y} x2={W} y2={y}
            stroke="var(--border)" strokeWidth="0.5"
          />
        );
      })}
      {/* Lane labels comp1..compN */}
      {Array.from({ length: numComps }, (_, k) => {
        const y = k * laneH + 14;
        return (
          <text
            key={`lbl-${k}`}
            x={6} y={y}
            fill="var(--muted)"
            fontSize={11}
            fontFamily="ui-monospace, SFMono-Regular, monospace"
          >comp{k + 1}</text>
        );
      })}
      {/* Polylines: one per (segment, component) */}
      {segments.flatMap((seg, sIdx) => {
        const xOff = sIdx * (dispLen + segmentGapPx);
        return Array.from({ length: numComps }, (_, k) => {
          const arr = seg.components[k];
          if (!arr || arr.length === 0) return null;
          const { mn, range } = ranges[k];
          const laneTop = k * laneH + traceTop;
          const pts = arr.map((v, i) => {
            const x = xOff + i;
            const y = laneTop + (1 - (v - mn) / range) * traceH;
            return `${x},${y.toFixed(2)}`;
          }).join(' ');
          return (
            <polyline
              key={`s${sIdx}-c${k}`}
              points={pts}
              fill="none"
              stroke="var(--accent)"
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />
          );
        });
      })}
    </svg>
  );

  if (segmentSec == null) return svg;
  return (
    <div>
      {svg}
      <TimeAxis
        segmentSec={segmentSec}
        numSegments={segments.length}
        segmentGapUnits={segmentGapPx}
        segmentWidthUnits={dispLen}
      />
    </div>
  );
}

/** 5-stop approximation of the viridis colormap. */
function viridis(t: number): [number, number, number] {
  const stops: Array<[number, [number, number, number]]> = [
    [0,    [68,  1,   84 ]],
    [0.25, [59,  82,  139]],
    [0.5,  [33,  144, 140]],
    [0.75, [93,  201, 99 ]],
    [1,    [253, 231, 37 ]],
  ];
  if (t <= 0) return stops[0][1];
  if (t >= 1) return stops[stops.length - 1][1];
  for (let i = 1; i < stops.length; i++) {
    if (t <= stops[i][0]) {
      const [t0, c0] = stops[i - 1];
      const [t1, c1] = stops[i];
      const f = (t - t0) / (t1 - t0);
      return [
        Math.round(c0[0] + (c1[0] - c0[0]) * f),
        Math.round(c0[1] + (c1[1] - c0[1]) * f),
        Math.round(c0[2] + (c1[2] - c0[2]) * f),
      ];
    }
  }
  return stops[stops.length - 1][1];
}
